// frontend/src/components/SupplyPoolsSection.js
import React from 'react';

const SupplyPoolsSection = ({
  supplyPools,
  supplyPoolForm,
  setSupplyPoolForm,
  editingSupplyPool,
  setEditingSupplyPool,
  onSaveSupplyPool,
  onDeleteSupplyPool,
  collections,
  jwtToken,
  existingDCLDeals,
  existingRecipes,
  mintLaunchingConfig
}) => {
  // Load items from a deal
  const loadItemsFromDeal = (dealId) => {
    const deal = existingDCLDeals?.find(d => d.id === parseInt(dealId));
    if (!deal) return;

    const items = [];
    if (deal.deal_items && Array.isArray(deal.deal_items)) {
      deal.deal_items.forEach(dealItem => {
        // Find collection item to get metadata
        const collectionItem = collections.find(c => 
          c.collection_address?.toLowerCase() === dealItem.dcl_collection_address?.toLowerCase() &&
          c.urn_item_id === dealItem.dcl_item_id
        );

        items.push({
          item_name: collectionItem?.title || `Item #${dealItem.dcl_item_id}`,
          collection_address: dealItem.dcl_collection_address || '',
          urn_item_id: dealItem.dcl_item_id || '',
          image_url: dealItem.deal_image || collectionItem?.image || '',
          quantity: 1 // Default quantity, user should update this
        });
      });
    }

    // Deduplicate by collection + urn_item_id
    const uniqueItems = [];
    const seen = new Set();
    items.forEach(item => {
      const key = `${item.collection_address}_${item.urn_item_id}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueItems.push(item);
      }
    });

    setSupplyPoolForm({
      ...supplyPoolForm,
      items: uniqueItems.length > 0 ? uniqueItems : [{ item_name: '', collection_address: '', urn_item_id: '', image_url: '', quantity: 1 }]
    });
  };

  // Load items from a recipe
  const loadItemsFromRecipe = (recipeId) => {
    const recipe = existingRecipes?.find(r => r.id === parseInt(recipeId));
    if (!recipe) return;

    const items = [];
    if (recipe.rewards && Array.isArray(recipe.rewards)) {
      recipe.rewards.forEach(reward => {
        // Find collection item to get metadata
        const collectionItem = collections.find(c => 
          c.collection_address?.toLowerCase() === reward.collection?.toLowerCase() &&
          c.urn_item_id === reward.item_id
        );

        items.push({
          item_name: collectionItem?.title || `Item #${reward.item_id}`,
          collection_address: reward.collection || '',
          urn_item_id: reward.item_id || '',
          image_url: collectionItem?.image || '',
          quantity: 1 // Default quantity, user should update this
        });
      });
    }

    // Deduplicate by collection + urn_item_id
    const uniqueItems = [];
    const seen = new Set();
    items.forEach(item => {
      const key = `${item.collection_address}_${item.urn_item_id}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueItems.push(item);
      }
    });

    setSupplyPoolForm({
      ...supplyPoolForm,
      items: uniqueItems.length > 0 ? uniqueItems : [{ item_name: '', collection_address: '', urn_item_id: '', image_url: '', quantity: 1 }]
    });
  };

  // Load items from MintLaunching paired deals and recipes
  const loadItemsFromMintLaunching = () => {
    if (!mintLaunchingConfig) return;

    const items = [];
    const seen = new Set();

    // Load from selected deals
    if (mintLaunchingConfig.selected_deal_ids && Array.isArray(mintLaunchingConfig.selected_deal_ids)) {
      mintLaunchingConfig.selected_deal_ids.forEach(dealId => {
        const deal = existingDCLDeals?.find(d => d.id === dealId);
        if (deal && deal.deal_items && Array.isArray(deal.deal_items)) {
          deal.deal_items.forEach(dealItem => {
            const key = `${dealItem.dcl_collection_address}_${dealItem.dcl_item_id}`;
            if (!seen.has(key)) {
              seen.add(key);
              const collectionItem = collections.find(c => 
                c.collection_address?.toLowerCase() === dealItem.dcl_collection_address?.toLowerCase() &&
                c.urn_item_id === dealItem.dcl_item_id
              );
              items.push({
                item_name: collectionItem?.title || `Item #${dealItem.dcl_item_id}`,
                collection_address: dealItem.dcl_collection_address || '',
                urn_item_id: dealItem.dcl_item_id || '',
                image_url: dealItem.deal_image || collectionItem?.image || ''
              });
            }
          });
        }
      });
    }

    // Load from selected recipes
    if (mintLaunchingConfig.selected_recipe_ids && Array.isArray(mintLaunchingConfig.selected_recipe_ids)) {
      mintLaunchingConfig.selected_recipe_ids.forEach(recipeId => {
        const recipe = existingRecipes?.find(r => r.id === recipeId);
        if (recipe && recipe.rewards && Array.isArray(recipe.rewards)) {
          recipe.rewards.forEach(reward => {
            const key = `${reward.collection}_${reward.item_id}`;
            if (!seen.has(key)) {
              seen.add(key);
              const collectionItem = collections.find(c => 
                c.collection_address?.toLowerCase() === reward.collection?.toLowerCase() &&
                c.urn_item_id === reward.item_id
              );
              items.push({
                item_name: collectionItem?.title || `Item #${reward.item_id}`,
                collection_address: reward.collection || '',
                urn_item_id: reward.item_id || '',
                image_url: collectionItem?.image || ''
              });
            }
          });
        }
      });
    }

    setSupplyPoolForm({
      ...supplyPoolForm,
      items: items.length > 0 ? items : [{ item_name: '', collection_address: '', urn_item_id: '', image_url: '', quantity: 1 }]
    });
  };

  // Add a new item to the supply pool form
  const addItemToPool = () => {
    setSupplyPoolForm({
      ...supplyPoolForm,
      items: [...supplyPoolForm.items, { item_name: '', collection_address: '', urn_item_id: '', image_url: '', quantity: 1 }]
    });
  };

  // Remove an item from the supply pool form
  const removeItemFromPool = (index) => {
    const newItems = supplyPoolForm.items.filter((_, i) => i !== index);
    setSupplyPoolForm({ ...supplyPoolForm, items: newItems });
  };

  // Update item field
  const updateItem = (index, field, value) => {
    const newItems = [...supplyPoolForm.items];
    newItems[index][field] = value;
    setSupplyPoolForm({ ...supplyPoolForm, items: newItems });
  };

  // Start editing a supply pool
  const startEdit = (pool) => {
    setEditingSupplyPool(pool.id);
    setSupplyPoolForm({
      name: pool.name,
      description: pool.description || '',
      total_supply: pool.total_supply.toString(),
      items: pool.items && pool.items.length > 0
        ? pool.items.map(item => ({
            item_name: item.item_name || '',
            collection_address: item.collection_address || '',
            urn_item_id: item.urn_item_id || '',
            image_url: item.image_url || '',
            quantity: item.quantity || 1,
            consumed: item.consumed || 0
          }))
        : [{ item_name: '', collection_address: '', urn_item_id: '', image_url: '', quantity: 1 }]
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingSupplyPool(null);
    setSupplyPoolForm({
      name: '',
      description: '',
      total_supply: '',
      items: [{ item_name: '', collection_address: '', urn_item_id: '', image_url: '', quantity: 1 }]
    });
  };

  return (
    <div className="admin-two-column">
      {/* Left column: Create/Edit Supply Pool */}
      <div className="admin-column-form">
        <h3>{editingSupplyPool ? '✏️ Edit Supply Pool' : '➕ Create New Supply Pool'}</h3>
        
        <label>
          <span style={{ fontWeight: 'bold' }}>Pool Name *</span>
          <input
            type="text"
            placeholder="e.g., Laser Eyes Supply - January 2025"
            value={supplyPoolForm.name}
            onChange={(e) => setSupplyPoolForm({ ...supplyPoolForm, name: e.target.value })}
            style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', fontSize: '1rem' }}
          />
        </label>

        <label style={{ marginTop: '1rem' }}>
          <span style={{ fontWeight: 'bold' }}>Description</span>
          <textarea
            placeholder="e.g., 50 Red + 50 Blue Laser Eyes"
            value={supplyPoolForm.description}
            onChange={(e) => setSupplyPoolForm({ ...supplyPoolForm, description: e.target.value })}
            style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', fontSize: '1rem', minHeight: '60px' }}
          />
        </label>

        <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(0, 123, 255, 0.1)', borderRadius: '0.5rem', border: '1px solid rgba(0, 123, 255, 0.3)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold' }}>Total Supply (auto-calculated)</span>
            <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#17a2b8' }}>
              {supplyPoolForm.items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)}
            </span>
          </div>
          <p style={{ fontSize: '0.85rem', color: '#aaa', margin: '0.5rem 0 0 0' }}>
            Sum of all item quantities below
          </p>
        </div>

        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0 }}>📦 Items in Pool</h4>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {/* Load from MintLaunching */}
              {mintLaunchingConfig && (
                <button
                  type="button"
                  onClick={loadItemsFromMintLaunching}
                  style={{
                    background: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    padding: '0.4rem 0.8rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap'
                  }}
                  title="Load all items from Mint Launching paired deals & recipes"
                >
                  🚀 Load from Mint Launching
                </button>
              )}
              
              {/* Load from Deal */}
              {existingDCLDeals && existingDCLDeals.length > 0 && (
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      loadItemsFromDeal(e.target.value);
                      e.target.value = ''; // Reset
                    }
                  }}
                  style={{
                    padding: '0.4rem 0.6rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    background: '#6f42c1',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <option value="">🏷️ Load from Deal...</option>
                  {existingDCLDeals.map(deal => (
                    <option key={deal.id} value={deal.id}>
                      {deal.deal_name || `Deal #${deal.id}`}
                    </option>
                  ))}
                </select>
              )}
              
              {/* Load from Recipe */}
              {existingRecipes && existingRecipes.length > 0 && (
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      loadItemsFromRecipe(e.target.value);
                      e.target.value = ''; // Reset
                    }
                  }}
                  style={{
                    padding: '0.4rem 0.6rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    background: '#e83e8c',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <option value="">🔥 Load from Recipe...</option>
                  {existingRecipes.map(recipe => (
                    <option key={recipe.id} value={recipe.id}>
                      {recipe.title || `Recipe #${recipe.id}`}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          
          {supplyPoolForm.items.map((item, index) => (
            <div key={index} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '1rem',
              borderRadius: '0.5rem',
              marginBottom: '0.75rem',
              position: 'relative'
            }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.9rem' }}>Item Name</label>
                <input
                  type="text"
                  placeholder="e.g., Red Laser Eyes"
                  value={item.item_name}
                  onChange={(e) => updateItem(index, 'item_name', e.target.value)}
                  style={{ width: '100%', padding: '0.4rem', marginTop: '0.2rem' }}
                />
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.9rem' }}>Collection Address</label>
                <input
                  type="text"
                  placeholder="0x..."
                  value={item.collection_address}
                  onChange={(e) => updateItem(index, 'collection_address', e.target.value)}
                  style={{ width: '100%', padding: '0.4rem', marginTop: '0.2rem' }}
                />
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.9rem' }}>URN Item ID</label>
                <input
                  type="text"
                  placeholder="e.g., 0"
                  value={item.urn_item_id}
                  onChange={(e) => updateItem(index, 'urn_item_id', e.target.value)}
                  style={{ width: '100%', padding: '0.4rem', marginTop: '0.2rem' }}
                />
              </div>
              
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#17a2b8' }}>
                  Quantity * 
                  {item.consumed > 0 && <span style={{ color: '#ffc107', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                    ({item.consumed} consumed)
                  </span>}
                </label>
                <input
                  type="number"
                  min={item.consumed || 1}
                  placeholder="e.g., 50"
                  value={item.quantity || ''}
                  onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '0.4rem', 
                    marginTop: '0.2rem',
                    border: '2px solid #17a2b8',
                    fontWeight: 'bold'
                  }}
                />
                <p style={{ fontSize: '0.75rem', color: '#aaa', margin: '0.2rem 0 0 0' }}>
                  How many of this specific item are available
                </p>
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.9rem' }}>Image URL (optional)</label>
                <input
                  type="text"
                  placeholder="https://..."
                  value={item.image_url}
                  onChange={(e) => updateItem(index, 'image_url', e.target.value)}
                  style={{ width: '100%', padding: '0.4rem', marginTop: '0.2rem' }}
                />
              </div>

              {supplyPoolForm.items.length > 1 && (
                <button
                  onClick={() => removeItemFromPool(index)}
                  style={{
                    position: 'absolute',
                    top: '0.5rem',
                    right: '0.5rem',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    padding: '0.25rem 0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  ✕ Remove
                </button>
              )}
            </div>
          ))}

          <button
            onClick={addItemToPool}
            style={{
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              width: '100%',
              fontSize: '1rem'
            }}
          >
            + Add Another Item
          </button>
        </div>

        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => onSaveSupplyPool(supplyPoolForm, editingSupplyPool)}
            className="rainbowBtn"
            style={{ flex: 1, padding: '0.75rem', fontSize: '1rem' }}
            disabled={!supplyPoolForm.name || !supplyPoolForm.total_supply}
          >
            {editingSupplyPool ? '💾 Update Supply Pool' : '✨ Create Supply Pool'}
          </button>

          {editingSupplyPool && (
            <button
              onClick={cancelEdit}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '1rem',
                padding: '0.75rem 1rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Right column: Existing Supply Pools */}
      <div className="admin-column-list">
        <h3>📦 Existing Supply Pools ({supplyPools.length})</h3>
        
        {supplyPools.length === 0 ? (
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '2rem',
            borderRadius: '0.5rem',
            textAlign: 'center',
            color: '#999'
          }}>
            <p>No supply pools yet. Create one to get started!</p>
            <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
              Supply pools let deals and recipes share the same supply limit.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {supplyPools.map(pool => {
              const percentUsed = pool.total_supply > 0 
                ? Math.round((pool.consumed / pool.total_supply) * 100) 
                : 0;
              const remaining = pool.total_supply - pool.consumed;
              
              return (
                <div key={pool.id} style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '1.1rem' }}>{pool.name}</h4>
                      {pool.description && (
                        <p style={{ margin: '0.25rem 0 0', color: '#999', fontSize: '0.9rem' }}>
                          {pool.description}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => startEdit(pool)}
                        style={{
                          background: '#ffc107',
                          color: '#000',
                          border: 'none',
                          borderRadius: '0.25rem',
                          padding: '0.4rem 0.8rem',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => onDeleteSupplyPool(pool.id)}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          padding: '0.4rem 0.8rem',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>

                  {/* Supply Stats */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.9rem', color: '#ccc' }}>Supply Usage:</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: percentUsed >= 90 ? '#dc3545' : percentUsed >= 75 ? '#ffc107' : '#28a745' }}>
                        {pool.consumed}/{pool.total_supply} ({percentUsed}%)
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${percentUsed}%`,
                        height: '100%',
                        background: percentUsed >= 90 ? '#dc3545' : percentUsed >= 75 ? '#ffc107' : '#28a745',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                    <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#999' }}>
                      {remaining} remaining
                    </div>
                  </div>

                  {/* Items */}
                  {pool.items && pool.items.length > 0 && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.9rem', color: '#ccc', marginBottom: '0.5rem' }}>Items:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {pool.items.map((item, idx) => {
                          const itemConsumed = item.consumed || 0;
                          const itemQuantity = item.quantity || 0;
                          const itemRemaining = itemQuantity - itemConsumed;
                          const itemPercentUsed = itemQuantity > 0 ? Math.round((itemConsumed / itemQuantity) * 100) : 0;
                          
                          return (
                            <div key={idx} style={{ 
                              background: 'rgba(0, 0, 0, 0.3)',
                              padding: '0.5rem',
                              borderRadius: '0.25rem',
                              border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                  {item.item_name || `Item #${item.urn_item_id}`}
                                </span>
                                <span style={{ 
                                  fontSize: '0.85rem', 
                                  fontWeight: 'bold',
                                  color: itemPercentUsed >= 90 ? '#dc3545' : itemPercentUsed >= 75 ? '#ffc107' : '#17a2b8'
                                }}>
                                  {itemConsumed}/{itemQuantity}
                                </span>
                              </div>
                              {item.urn_item_id && (
                                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                                  URN: {item.urn_item_id}
                                </div>
                              )}
                              <div style={{
                                width: '100%',
                                height: '4px',
                                background: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '2px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${itemPercentUsed}%`,
                                  height: '100%',
                                  background: itemPercentUsed >= 90 ? '#dc3545' : itemPercentUsed >= 75 ? '#ffc107' : '#17a2b8',
                                  transition: 'width 0.3s ease'
                                }}></div>
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                                {itemRemaining} remaining
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Linked Deals/Recipes */}
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: '#999' }}>
                    <div>
                      🏷️ Linked Deals: <span style={{ color: '#fff', fontWeight: 'bold' }}>
                        {pool.linked_deals_count || 0}
                      </span>
                    </div>
                    <div>
                      🔥 Linked Recipes: <span style={{ color: '#fff', fontWeight: 'bold' }}>
                        {pool.linked_recipes_count || 0}
                      </span>
                    </div>
                  </div>

                  {pool.created_at && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#666' }}>
                      Created: {new Date(pool.created_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplyPoolsSection;

